import { apiClient } from "@/services/api"
import { defineStore } from "pinia"

// The request in flight, shared by every consumer: the general tab's select and the overview's kind
// column want the list at the same time. Same shape as `customer-contactposition.js`.
let inFlight = null

/**
 * The kinds a supplier can be ("Art": Baustoffhandel, Mietpark, …). Seeded per workspace and edited from
 * the supplier's kind select, like a customer contact's position.
 */
export const useSupplierKindStore = defineStore("supplierKind", {
  state: () => ({
    kinds: [],
    loaded: false,
    failed: false,
  }),
  getters: {
    getKinds: state => state.kinds,
    isLoaded: state => state.loaded,
    /** The name behind a kind reference — an id or the `{ id, name }` a supplier answers with — or null. */
    getKindName: state => reference => {
      if (!reference) return null
      if (typeof reference === "object") return reference.name || null
      return state.kinds.find(kind => kind.id === reference)?.name ?? null
    },
  },
  actions: {
    setKinds(kinds) {
      this.kinds = [...kinds]
    },
    /** Called on sign-out: kinds belong to a workspace. */
    reset() {
      inFlight = null
      this.$reset()
    },
    /** Loads the list once for the whole app; retries only when the previous attempt failed. */
    ensureLoaded() {
      if (inFlight) return inFlight
      if (this.loaded && !this.failed) return Promise.resolve()

      inFlight = this.loadKinds().finally(() => {
        inFlight = null
      })
      return inFlight
    },
    loadKinds() {
      return apiClient
        .get("/supplier/kind", { headers: { tenant: true, authorization: true } })
        .then(res => {
          if (res.status === 200) {
            this.setKinds(res.data)
            this.failed = false
          }
          return res
        })
        .catch(() => {
          this.failed = true
        })
        .finally(() => {
          this.loaded = true
        })
    },
    createKind(kind) {
      return apiClient.post("/supplier/kind", kind, { headers: { tenant: true, authorization: true } })
    },
    updateKind({ id, kind }) {
      return apiClient.put(`/supplier/kind/${id}`, kind, { headers: { tenant: true, authorization: true } })
    },
    deleteKind(id) {
      return (
        apiClient
          .delete(`/supplier/kind/${id}`, { headers: { tenant: true, authorization: true } })
          // The response interceptor raises the violation; the caller still inspects the status.
          .catch(err => err.response)
      )
    },
  },
})
